import { WORLD_H, WORLD_W, type ExplosionCircle, type ThemeId, THEMES } from '@cuboom/shared';

function hexToRgb(hex: string): [number, number, number] {
  const v = parseInt(hex.slice(1), 16);
  return [(v >> 16) & 255, (v >> 8) & 255, v & 255];
}

function hash2(x: number, y: number): number {
  let t = (x * 374761393 + y * 668265263) | 0;
  t = Math.imul(t ^ (t >>> 13), 1274126177);
  return ((t ^ (t >>> 16)) >>> 0) / 4294967296;
}

/** Themes whose raster gets a second-pass surface treatment. */
const TREATED = new Set<ThemeId>([
  'city',
  'skyline',
  'neon',
  'desert',
  'lagoon',
  'snow',
  'canyon',
  'sunset',
  'mint',
  'prism',
  'magma',
  'mono',
]);

/** Prism's column sweep: pink → cyan → violet. */
const PRISM_STOPS: [number, number, number][] = [
  [255, 111, 177],
  [89, 216, 255],
  [184, 140, 255],
];

/**
 * Rasterizes the sim's terrain mask into an offscreen canvas once, then keeps
 * it in sync with incremental `destination-out` carves.
 */
export class TerrainLayer {
  readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;

  constructor(terrain: Uint8Array, themeId: ThemeId) {
    this.canvas = document.createElement('canvas');
    this.canvas.width = WORLD_W;
    this.canvas.height = WORLD_H;
    const ctx = this.canvas.getContext('2d');
    if (!ctx) throw new Error('2d context unavailable');
    this.ctx = ctx;
    this.rasterize(terrain, themeId);
  }

  private rasterize(terrain: Uint8Array, themeId: ThemeId): void {
    const theme = THEMES[themeId];
    const grass = hexToRgb(theme.grass);
    const grassDark = hexToRgb(theme.grassDark);
    const dirt = hexToRgb(theme.dirt);
    const dirtDark = hexToRgb(theme.dirtDark);
    const speckle = hexToRgb(theme.speckle);

    // First solid y per column (global surface).
    const surface = new Int16Array(WORLD_W).fill(-1);
    for (let x = 0; x < WORLD_W; x++) {
      for (let y = 0; y < WORLD_H; y++) {
        if (terrain[y * WORLD_W + x] === 1) {
          surface[x] = y;
          break;
        }
      }
    }

    const img = this.ctx.createImageData(WORLD_W, WORLD_H);
    const data = img.data;

    for (let y = 0; y < WORLD_H; y++) {
      for (let x = 0; x < WORLD_W; x++) {
        const i = y * WORLD_W + x;
        if (terrain[i] !== 1) continue; // alpha stays 0
        const surf = surface[x];
        const depth = surf >= 0 ? y - surf : 0;
        const edgeNoise = (hash2(x, 0) - 0.5) * 8;

        let r: number, g: number, b: number;
        if (depth < 10 + edgeNoise) {
          // Top grass band.
          r = grass[0];
          g = grass[1];
          b = grass[2];
        } else if (depth < 17 + edgeNoise) {
          const t = (depth - 10 - edgeNoise) / 7;
          r = grass[0] + (grassDark[0] - grass[0]) * t;
          g = grass[1] + (grassDark[1] - grass[1]) * t;
          b = grass[2] + (grassDark[2] - grass[2]) * t;
        } else {
          const t = Math.min(1, (depth - 17) / 420);
          r = dirt[0] + (dirtDark[0] - dirt[0]) * t;
          g = dirt[1] + (dirtDark[1] - dirt[1]) * t;
          b = dirt[2] + (dirtDark[2] - dirt[2]) * t;
        }

        // Speckles deep in the dirt.
        if (depth > 24 && hash2(x * 7, y * 13) > 0.988) {
          r = speckle[0];
          g = speckle[1];
          b = speckle[2];
        }

        // Side-wall shading: columns exposed on the right get darker.
        const rightSolid = x + 1 < WORLD_W ? terrain[i + 1] === 1 : false;
        if (!rightSolid) {
          r *= 0.82;
          g *= 0.82;
          b *= 0.82;
        }

        const o = i * 4;
        data[o] = r;
        data[o + 1] = g;
        data[o + 2] = b;
        data[o + 3] = 255;
      }
    }
    this.applySurfaceTreatment(data, surface, themeId);
    this.ctx.putImageData(img, 0, 0);
  }

  /**
   * Second pass — per-theme surface detail on top of the base bands:
   * pavement seams, sand ripples, snow sparkle, strata, prism color bands,
   * magma cracks, mono grain. Runs once per map (carves later punch through
   * these pixels like any other).
   */
  private applySurfaceTreatment(
    data: Uint8ClampedArray,
    surface: Int16Array,
    themeId: ThemeId,
  ): void {
    if (!TREATED.has(themeId)) return; // themes that keep the plain base bands
    const pixel = (x: number, y: number): number => (y * WORLD_W + x) * 4;
    const shade = (o: number, mul: number): void => {
      data[o] *= mul;
      data[o + 1] *= mul;
      data[o + 2] *= mul;
    };
    const mixInto = (o: number, r: number, g: number, b: number, k: number): void => {
      data[o] += (r - data[o]) * k;
      data[o + 1] += (g - data[o + 1]) * k;
      data[o + 2] += (b - data[o + 2]) * k;
    };
    const speck = hexToRgb(THEMES[themeId].speckle);

    for (let x = 0; x < WORLD_W; x++) {
      const surf = surface[x];
      if (surf < 0) continue;
      for (let y = surf; y < WORLD_H; y++) {
        const o = pixel(x, y);
        if (data[o + 3] !== 255) continue; // sky / carved out
        const depth = y - surf;

        switch (themeId) {
          case 'city':
          case 'skyline': {
            // Curb highlight on the crown, expansion seam every 26px.
            if (depth === 0 && x % 26 !== 0) shade(o, 1.1);
            else if (x % 26 === 0 && depth < 6) shade(o, 0.68);
            break;
          }
          case 'neon': {
            // Wet-look crown catching the signs: speckle glow on top.
            if (depth === 0) mixInto(o, speck[0], speck[1], speck[2], 0.35);
            else if (x % 26 === 0 && depth < 6) shade(o, 0.68);
            break;
          }
          case 'desert':
          case 'lagoon': {
            // Wind ripples combed through the top crust.
            if (depth < 22) shade(o, 1 + Math.sin(depth * 0.9 + x * 0.12) * 0.06);
            break;
          }
          case 'snow': {
            // Sun glints in the powder.
            if (depth < 36 && hash2(x * 13, y * 7) > 0.985) {
              data[o] = 255;
              data[o + 1] = 255;
              data[o + 2] = 255;
            }
            break;
          }
          case 'canyon':
          case 'sunset':
          case 'mint': {
            // Sediment strata: darker bands through the rock.
            if (depth >= 17 && Math.sin(depth * 0.11) > 0.72) shade(o, 0.9);
            break;
          }
          case 'prism': {
            // Full-height hue sweep: pink → cyan → violet by column,
            // mixed over the base so shading and speckles still read.
            const t = x / WORLD_W;
            const [c0, c1, c2] = PRISM_STOPS;
            const seg = t < 0.5 ? t * 2 : (t - 0.5) * 2;
            const a = t < 0.5 ? c0 : c1;
            const b = t < 0.5 ? c1 : c2;
            mixInto(
              o,
              a[0] + (b[0] - a[0]) * seg,
              a[1] + (b[1] - a[1]) * seg,
              a[2] + (b[2] - a[2]) * seg,
              0.72,
            );
            break;
          }
          case 'magma': {
            if (depth < 10) shade(o, 0.75); // charred crust
            else if (depth < 420 && hash2(x, Math.floor(y / 24)) > 0.992) {
              // Glowing fissures: short vertical dashes toward the ember hue.
              mixInto(o, speck[0], speck[1], speck[2], 0.8);
            }
            break;
          }
          case 'mono': {
            if (depth === 0) shade(o, 0.55); // ink outline on the crown
            else if (depth >= 10) {
              const n = (hash2(x * 3, y * 5) - 0.5) * 12;
              data[o] += n;
              data[o + 1] += n;
              data[o + 2] += n;
            }
            break;
          }
        }
      }
    }
  }

  /** Incrementally punch a crater (matches sim carve circles 1:1). */
  carve(circle: ExplosionCircle): void {
    this.ctx.save();
    this.ctx.globalCompositeOperation = 'destination-out';
    this.ctx.beginPath();
    this.ctx.arc(circle.x, circle.y, circle.r, 0, Math.PI * 2);
    this.ctx.fill();
    this.ctx.restore();
  }

  carveAll(circles: ExplosionCircle[]): void {
    for (const c of circles) this.carve(c);
  }
}
